import { NavLink, useParams } from "react-router";
import { ReactNode, useCallback, useState } from "react";
import {
  MilestonePage,
  MilestonePageContent,
  MilestonePageHeader,
  MilestonePageTitle,
} from "@/shared/bsm/core/milestone/components/ui/milestone-page";
import t from "@/lang/lang";
import {
  CommitmentsTable,
  CommitmentsTableFilters,
} from "@/services/backend/relationships/components/commitments-table";
import { ListCommitmentsRequest } from "@/services/backend/relationships/api/service";
import {
  Commitment,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import { CreateCommitmentsFromConfirmationDialog } from "@/routes/processes/[processId]/rlt/commitments/_components/create-commitments-from-confirmation-dialog";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { ServiceObjectLink } from "@/routes/_components/links/service-object-link";
import {
  BreadcrumbItem,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { RltBreadcrumbs } from "@/routes/processes/[processId]/rlt/_components/rlt-breadcrumbs";

export function CommitmentsRoute() {
  const { processId } = useParams();
  const [request, setRequest] = useState<ListCommitmentsRequest>({
    process: {
      active: true,
      values: [processId!],
    },
    offset: 0,
    limit: 10,
  });
  const [selection, setSelection] = useState<Commitment[]>([]);

  const { data: process } = useProcessesProcessShowQuery({ id: processId! });

  const renderCommitmentLink = useCallback(
    (
      commitment: Commitment,
      relationship: Relationship,
      children: ReactNode,
    ) =>
      commitment.processId !== null ? (
        <NavLink
          to={`/processes/${commitment.serviceKind}/${commitment.processId}/commitments/${commitment.relationshipId}/${commitment.id}`}
        >
          {children}
        </NavLink>
      ) : (
        <NavLink
          to={`/customers/${relationship.customerId}/relationships/${commitment.relationshipId}/commitments/${commitment.id}`}
        >
          {children}
        </NavLink>
      ),
    [],
  );

  if (!process) return null;

  const breadcrumbs = (
    <RltBreadcrumbs customerId={process.customerId} processId={process.id}>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>{t("Sollstellungen")}</BreadcrumbPage>
      </BreadcrumbItem>
    </RltBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      <MilestonePage>
        <MilestonePageHeader>
          <MilestonePageTitle>{t("Sollstellungen")}</MilestonePageTitle>
        </MilestonePageHeader>
        <MilestonePageContent>
          <div className="grid gap-2">
            <div className="flex justify-between">
              <CommitmentsTableFilters
                request={request}
                onRequestChange={setRequest}
                selection={selection}
                onSelectionChange={setSelection}
                hideServiceObjectFilter={false}
              />
              <CreateCommitmentsFromConfirmationDialog process={process} />
            </div>
            <CommitmentsTable
              request={request}
              onRequestChange={setRequest}
              selection={selection}
              onSelectionChange={setSelection}
              showBorder
              hideServiceObject={false}
              renderCommitmentLink={renderCommitmentLink}
              renderDocumentLink={(path, children) => (
                <NavLink to={path}>{children}</NavLink>
              )}
              renderServiceObjectLink={(serviceObjectId) => (
                <ServiceObjectLink id={serviceObjectId} />
              )}
            />
          </div>
        </MilestonePageContent>
      </MilestonePage>
    </ProcessPageMainTemplate>
  );
}
